import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { GiftStatus } from 'src/admin-entity/enum/order/gift/gift-status.type';
import { OrderItemProductStatusSearch } from 'src/admin-entity/enum/order/order-item-product-status-search.type';
import { GiftOrderItem } from 'src/admin-entity/interface/order/gift/gift-order-item.interface';
import { Page } from 'src/admin-entity/interface/page/page-info.interface';
import client from 'src/api/client';
import {
    getGiftHistory,
    getGiftItemDetail,
    getGiftPriceHistory,
    updateGiftMemo,
    withdrawGiftGroupAllOrderItem,
} from 'src/api/gift';
import { BASE_URL } from 'src/config/api';
import { AppState } from 'src/store';
import { dateFormatter } from 'src/utils/dateFormatter';
import { getErrorMessage } from 'src/utils/utilService';
import { setSnackbarMessage } from '../snackbarMessage/snackbarSlice';
import { initialState } from './state';

export const searchGiftList = createAsyncThunk(
    `gift/searchGiftList`,
    async (_, { getState, rejectWithValue, dispatch }) => {
        try {
            const { searchState } = (getState() as AppState).gift;

            const url = new URL(`${BASE_URL}/v1/admin/gift/group?page=${searchState.page}&take=${searchState.take}`);

            url.searchParams.set('beginDate', dateFormatter(new Date(searchState.beginDate), 'yyyy-MM-DD'));
            url.searchParams.set('endDate', dateFormatter(new Date(searchState.endDate), 'yyyy-MM-DD'));

            if (searchState.productName) {
                url.searchParams.set('productName', searchState.productName);
            }

            if (searchState.searchText && searchState.selectedTopic.value) {
                url.searchParams.set(searchState.selectedTopic.value, searchState.searchText);
            }

            if (searchState.name) {
                url.searchParams.set('name', searchState.name);
            }

            if (searchState.phone) {
                url.searchParams.set('phone', searchState.phone);
            }

            if (0 < searchState.orderItemProductStatus.length) {
                url.searchParams.set('orderItemProductStatus', searchState.orderItemProductStatus.join('|'));
            }

            if (0 < searchState.giftStatus.length) {
                url.searchParams.set('giftStatus', searchState.giftStatus.join('|'));
            }

            const res: AxiosResponse<Page<GiftOrderItem>> = await client(url.toString());

            return {
                data: res.data,
                url: `/order/gift/list${url.search}`,
            };
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
        }
    },
);

export const getGiftDetail = createAsyncThunk(
    `gift/getGiftDetail`,
    async (id: number, { dispatch, rejectWithValue }) => {
        try {
            console.log('a');
            const res = await getGiftItemDetail(id);
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
        }
    },
);

export const getGiftDetailPriceHistory = createAsyncThunk(
    `gift/giftDetailPriceHistory`,
    async (id: number, { dispatch, rejectWithValue }) => {
        try {
            const res = await getGiftPriceHistory(id);
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(getErrorMessage(err.response.data.message));
        }
    },
);

export const updateGiftAdminMemo = createAsyncThunk(
    `gift/updateGiftAdminMemo`,
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            const { giftOrderItemDetail } = (getState() as AppState).gift;
            const res = await updateGiftMemo(giftOrderItemDetail.id, giftOrderItemDetail.adminMemo);
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(getErrorMessage(err.response.data.message));
        }
    },
);

export const getGiftOrderItemHistory = createAsyncThunk(
    `gift/getGiftOrderItemHistory`,
    async (id: number, { dispatch, rejectWithValue }) => {
        try {
            const res = await getGiftHistory(id);
            return res.data;
        } catch (err) {
            dispatch(setSnackbarMessage(getErrorMessage(err)));
            return rejectWithValue(getErrorMessage(err.response.data.message));
        }
    },
);

export const withdrawGiftGroup = createAsyncThunk(`gift/withdrawGiftGroup`, async (id: number, { rejectWithValue }) => {
    try {
        await withdrawGiftGroupAllOrderItem(id);
        return id;
    } catch (err) {
        return rejectWithValue(err);
    }
});

export const giftSlice = createSlice({
    name: 'giftSlice',
    initialState,
    reducers: {
        initGiftSearchState: () => {
            return { ...initialState };
        },
        setTake: (state, action: PayloadAction<number>) => {
            state.searchState.take = action.payload;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.searchState.page = action.payload;
        },
        setTopic: (state, action: PayloadAction<{ id: number; name: string; value: string }>) => {
            state.searchState.selectedTopic = action.payload;
        },
        setProductName: (state, action: PayloadAction<string>) => {
            state.searchState.productName = action.payload;
        },
        setSearchText: (state, action: PayloadAction<string>) => {
            state.searchState.searchText = action.payload;
        },
        setName: (state, action: PayloadAction<string>) => {
            state.searchState.name = action.payload;
        },
        setPhone: (state, action: PayloadAction<string>) => {
            state.searchState.phone = action.payload;
        },
        setBeginDate: (state, action: PayloadAction<string>) => {
            state.searchState.beginDate = action.payload;
        },
        setEndDate: (state, action: PayloadAction<string>) => {
            state.searchState.endDate = action.payload;
        },
        setGiftStatus: (state, action: PayloadAction<GiftStatus[]>) => {
            state.searchState.giftStatus = action.payload;
        },
        setOrderItemProductStatus: (state, action: PayloadAction<OrderItemProductStatusSearch[]>) => {
            state.searchState.orderItemProductStatus = action.payload;
        },
        setSimpleDate: (state, action: PayloadAction<string>) => {
            const date = new Date();

            let beginDate: Date;
            let endDate: Date;

            if (action.payload === 'today') {
                beginDate = date;
                endDate = date;
            } else if (action.payload === 'yesterday') {
                beginDate = new Date(date.setDate(date.getDate() - 1));
                endDate = new Date();
            } else if (action.payload === 'week') {
                beginDate = new Date(date.setDate(date.getDate() - 7));
                endDate = new Date();
            } else if (action.payload === 'month') {
                beginDate = new Date(date.setMonth(date.getMonth() - 1));
                endDate = new Date();
            } else if (action.payload === '3month') {
                beginDate = new Date(date.setMonth(date.getMonth() - 3));
                endDate = new Date();
            } else if (action.payload === '6month') {
                beginDate = new Date(date.setMonth(date.getMonth() - 6));
                endDate = new Date();
            } else {
                beginDate = date;
                endDate = date;
            }
            state.searchState.beginDate = beginDate.toString();
            state.searchState.endDate = endDate.toString();
        },
        setGiftAdminMemo: (state, action: PayloadAction<string>) => {
            state.giftOrderItemDetail.adminMemo = action.payload;
        },
        setSelectedMemoContents: (state, action: PayloadAction<string>) => {
            state.selectedMemoContents = action.payload;
        },
        setSelectedGiftId: (state, action: PayloadAction<number>) => {
            state.selectedGiftId = action.payload;
        },
        setSelectedGiftGroupId: (state, action: PayloadAction<number>) => {
            state.selectedGiftGroupId = action.payload;
        },
        setIsMemoOpen: (state, action: PayloadAction<boolean>) => {
            state.isMemoOpen = action.payload;
        },
        setIsOpenWithdrawModal: (state, action: PayloadAction<boolean>) => {
            state.isOpenWithdrawModal = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(searchGiftList.fulfilled, (state, { payload }) => {
            state.list = payload.data.list;
            state.page = payload.data.page;
        });
        builder.addCase(getGiftDetail.fulfilled, (state, { payload }) => {
            state.giftOrderItemDetail = payload;
        });
        builder.addCase(getGiftDetailPriceHistory.fulfilled, (state, { payload }) => {
            state.giftOrderPriceHistory = payload;
        });
        builder.addCase(updateGiftAdminMemo.fulfilled, (state, { payload }) => {
            console.log(payload);
        });
        builder.addCase(getGiftOrderItemHistory.fulfilled, (state, { payload }) => {
            state.giftOrderHistory = payload;
        });
        builder.addCase(withdrawGiftGroup.fulfilled, (state, { payload }) => {
            console.log(payload);
        });
    },
});

export default giftSlice.reducer;
export const {
    setTake,
    setBeginDate,
    setEndDate,
    setGiftStatus,
    setName,
    setPage,
    setPhone,
    setSearchText,
    setTopic,
    setProductName,
    setOrderItemProductStatus,
    initGiftSearchState,
    setSimpleDate,
    setGiftAdminMemo,
    setIsMemoOpen,
    setIsOpenWithdrawModal,
    setSelectedGiftGroupId,
    setSelectedGiftId,
    setSelectedMemoContents,
} = giftSlice.actions;
